import { Link } from '@tanstack/react-router'
import {
  Button,
  Menu,
  MenuItem,
  Text as MenuText,
  MenuTrigger,
} from '@react-spectrum/s2'
import Delete from '@react-spectrum/s2/icons/Delete'
import Add from '@react-spectrum/s2/icons/Add'
import { Ellipsis, MessageSquare } from 'lucide-react'
import { trackAnalyticsEvent } from '@client/analytics'
import { fullWidthCenteredButtonStyles } from '@/styles/s2-macros'
import { cn } from '@/lib/utils'

interface ChatSidebarProps {
  history: Array<any>
  activeConversationId?: string
  onNewChat: () => void
  onDeleteConversation: (conversationId: string) => void
}

export function ChatSidebar({
  history,
  activeConversationId,
  onNewChat,
  onDeleteConversation,
}: ChatSidebarProps) {
  return (
    <div className="hidden h-full w-72 shrink-0 flex-col border-r border-border/80 bg-background p-4 shadow-sm lg:flex">
      <div className="mb-6 pt-1">
        <Button
          size="L"
          styles={fullWidthCenteredButtonStyles}
          onPress={() => {
            trackAnalyticsEvent('new_chat_clicked', {
              location: 'desktop_sidebar',
              history_count: history.length,
            })
            onNewChat()
          }}
        >
          <Add />
          New chat
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto space-y-1">
        <div className="mb-2 px-2 text-xs font-medium text-muted-foreground">
          Recent chats
        </div>
        {history.length === 0 && (
          <p className="py-4 text-center text-sm text-muted-foreground">
            No history yet.
          </p>
        )}
        {history.map((chat) => (
          <div
            key={chat.id}
            className={cn(
              'group flex items-center gap-1 rounded-md transition-colors',
              activeConversationId === chat.id
                ? 'bg-secondary'
                : 'hover:bg-secondary/60',
            )}
          >
            <Link
              to="/chat"
              search={{ conversationId: chat.id }}
              onClick={() =>
                trackAnalyticsEvent('conversation_selected', {
                  conversation_id: chat.id,
                  location: 'desktop_sidebar',
                  is_active: activeConversationId === chat.id,
                })
              }
              className={cn(
                'flex min-w-0 flex-1 items-center gap-2 truncate p-2 text-left text-sm transition-colors',
                activeConversationId === chat.id
                  ? 'font-medium text-foreground'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              <MessageSquare className="h-3.5 w-3.5 shrink-0" />
              <span className="min-w-0 flex-1 truncate">
                <span className="block truncate">
                  {chat.title || 'Untitled chat'}
                </span>
              </span>
            </Link>
            <MenuTrigger align="end">
              <div className="invisible mr-1 opacity-0 transition-opacity group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100">
                <Button
                  variant="secondary"
                  fillStyle="outline"
                  size="S"
                  aria-label={`Open conversation actions for ${chat.title || 'Untitled chat'}`}
                >
                  <Ellipsis className="h-3.5 w-3.5" />
                </Button>
              </div>
              <Menu
                aria-label="Conversation actions"
                size="M"
                className="min-w-[10rem]"
              >
                <MenuItem
                  onAction={() => {
                    trackAnalyticsEvent('conversation_deleted', {
                      conversation_id: chat.id,
                      location: 'desktop_sidebar',
                      is_active: activeConversationId === chat.id,
                    })
                    onDeleteConversation(chat.id)
                  }}
                >
                  <Delete />
                  <MenuText slot="label">Delete</MenuText>
                </MenuItem>
              </Menu>
            </MenuTrigger>
          </div>
        ))}
      </div>
    </div>
  )
}
